import { formatAmount } from '@repo/ui/amount-text';
import { type TranslationType } from '@/lib/types';

// Constants
export const TAB_KEYS = {
  CASINO_BETS: 'CASINO_BETS',
  SPORTS_BETS: 'SPORTS_BETS',
} as const;

export type tabKeyType = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

export const getTabs = (t: TranslationType) => [
  { key: TAB_KEYS.CASINO_BETS, label: t('tab.casino-bets') },
  { key: TAB_KEYS.SPORTS_BETS, label: t('tab.sports-bets') },
];

// Dynamic name generation
const NAME_PREFIXES = [
  'Ace',
  'Alpha',
  'Blaze',
  'Bolt',
  'Cyber',
  'Dark',
  'Dragon',
  'Fire',
  'Flash',
  'Ghost',
  'Gold',
  'Iron',
  'Laser',
  'Lightning',
  'Mega',
  'Neo',
  'Night',
  'Nova',
  'Omega',
  'Power',
  'Quantum',
  'Rapid',
  'Shadow',
  'Silver',
  'Sonic',
  'Steel',
  'Storm',
  'Super',
  'Thunder',
  'Turbo',
  'Ultra',
  'Viper',
  'Wild',
  'Zero',
  'Cosmic',
  'Electric',
  'Mystic',
  'Titan',
  'Phoenix',
  'Vortex',
];

const NAME_SUFFIXES = [
  'Hunter',
  'Rider',
  'Master',
  'Force',
  'Strike',
  'Fury',
  'Storm',
  'Blaze',
  'Wolf',
  'Eagle',
  'Hawk',
  'Tiger',
  'Lion',
  'Bear',
  'Shark',
  'Viper',
  'Ninja',
  'Warrior',
  'Knight',
  'Wizard',
  'Ranger',
  'Scout',
  'Sniper',
  'Racer',
  'Pilot',
  'Hero',
  'Legend',
  'Champion',
  'King',
  'Queen',
  'Prince',
  'Duke',
  'Lord',
  'Boss',
  'Chief',
  'Captain',
  'General',
  'Admiral',
  'Commander',
  'Ace',
];

const EMOJIS = [
  '🔥',
  '⚡',
  '🦅',
  '🥷',
  '🐺',
  '🎵',
  '🔮',
  '💎',
  '🚀',
  '🧘',
  '💥',
  '⭐',
  '🌟',
  '🎯',
  '🏆',
  '👑',
  '💪',
  '🎮',
  '🃏',
  '🎲',
];

const NUMBERS = [
  '777',
  '88',
  '99',
  '21',
  '007',
  '2024',
  '888',
  '666',
  '420',
  '69',
  '13',
  '42',
  '123',
  '999',
  '101',
  '404',
  '911',
  '555',
];

function generateRandomName(): string {
  const useEmoji = Math.random() < 0.3; // 30% chance for emoji
  const useNumber = Math.random() < 0.4; // 40% chance for number suffix

  let name = getRandom(NAME_PREFIXES) + getRandom(NAME_SUFFIXES);

  if (useNumber) {
    name += getRandom(NUMBERS);
  }

  if (useEmoji) {
    name = getRandom(EMOJIS) + ' ' + name;
  }

  return name;
}

// Mock data - existing players
const EXISTING_PLAYERS = [
  '🔥 Vardan_777',
  'NightHawk92',
  '🦅 GoldenEagle',
  'BlazeFury',
  'ShadowRunner',
  '⚡ VelocityMax',
  'ThunderBolt88',
  '🥷 CyberNinja',
  'StarGazer',
  '🐺 IronWolf',
  'QuantumRider',
  '⚡ NeonStorm',
  'AlphaStrike',
  'CosmicDrift',
  '🎵 TurboVibe',
  'ElectricSoul',
  '🔮 MysticForce',
  'SilverBullet',
  '💎 DiamondEdge',
  'FireStorm99',
  'AceVentura',
  '🚀 RocketBoost',
  'LightSpeed',
  'StealthMode',
  'PowerSurge',
  'VortexHunter',
  'CrimsonWave',
  '🧘 ZenMaster',
  'TitanForce',
  '💥 NovaBurst',
];

// Generate additional random players
const GENERATED_PLAYERS = Array.from({ length: 20 }, () => generateRandomName());

// Combine existing and generated players
const PLAYERS = [...EXISTING_PLAYERS, ...GENERATED_PLAYERS];

const ORIGINAL_GAMES = ['Coin Flip', 'Dice', 'Crash', 'Wheel'];

const SLOT_GAMES = [
  'Sweet Bonanza',
  'Aviatrix',
  'Gates of Olympus',
  'Spaceman',
  'Battle Tanks',
  'Royal Griffin',
  'Majestic Wolf',
  'Sweet Bonanza Dice',
  'Book of Tombs',
  'VIP Filthy Riches',
  'Gunspinner',
  'War Turtle',
  'Squid Slot',
  'Aviamasters',
  'Totem',
  "Gonzo's Gold",
  "Draco's Gold",
  'Asgard Legends',
  "Dragon's Luck Power Reels",
  'Fortune House',
  "Dragon's Fire",
  'Magic Gate',
  'Lucky Fortune Cat',
  "Pirates' Plenty Battle for Gold",
  "Dragon's Luck Deluxe",
  '10,001 Nights',
  'Epic Journey',
  'Golden Temple',
  'Phoenix Fire Power Reels',
  'Chinese Treasures',
];

const LIVE_CASINO_GAMES = [
  'Live Blackjack Classic',
  'Live Roulette European',
  'Live Baccarat Supreme',
  "Live Casino Hold'em",
  'Live Three Card Poker',
  'Live Dragon Tiger',
  'Live Andar Bahar',
  'Live Teen Patti',
  'Live Sic Bo',
  'Live Lightning Roulette',
  'Live Immersive Roulette',
  'Live Speed Roulette',
  'Live Auto Roulette',
  'Live VIP Blackjack',
  'Live Infinite Blackjack',
  'Live Speed Blackjack',
  'Live Lightning Blackjack',
  'Live Baccarat Squeeze',
  'Live Lightning Baccarat',
  'Live Crazy Time',
  'Live Mega Ball',
  'Live Deal or No Deal',
  'Live Monopoly Live',
  'Live Dream Catcher',
  'Live Cash or Crash',
];

const ROULETTE_GAMES = [
  'European Roulette',
  'American Roulette',
  'French Roulette',
  'Multi-Wheel Roulette',
  'Speed Roulette',
  'Mini Roulette',
  'Double Ball Roulette',
  'Immersive Roulette',
  'Lightning Roulette',
  'Auto Roulette',
  'VIP Roulette',
  'Premium European Roulette',
  'Premium American Roulette',
  'Premium French Roulette',
  'Roulette Royal',
  'Golden Chip Roulette',
  'Diamond VIP Roulette',
  'Salon Privé Roulette',
  'Live Roulette Pro',
  'Turbo Roulette',
];

const CASINO_GAMES = [...ORIGINAL_GAMES, ...SLOT_GAMES, ...LIVE_CASINO_GAMES, ...ROULETTE_GAMES];

const SPORT_EVENT_MAP: Record<string, string[]> = {
  Soccer: [
    'UEFA Champions League',
    'English Premier League',
    'Spanish La Liga',
    'Italian Serie A',
    'German Bundesliga',
    'French Ligue 1',
    'UEFA Europa League',
    'Copa Libertadores',
    'FIFA World Cup',
    'Euro Championship',
    'Portuguese Primeira Liga',
    'Dutch Eredivisie',
    'Brazilian Serie A',
    'Argentine Primera División',
    'Mexican Liga MX',
    'Major League Soccer',
    'Copa America',
    'African Cup of Nations',
    'Asian Cup',
    'CONCACAF Gold Cup',
  ],
  Basketball: [
    'NBA',
    'EuroLeague',
    'Turkish BSL',
    'ACB League',
    'ABA League',
    'NCAA March Madness',
    'FIBA World Cup',
    'Olympic Basketball',
    'NBA G League',
    'VTB United League',
    'Greek Basket League',
    'Italian Lega Basket',
    'French LNB Pro A',
    'German BBL',
    'WNBA',
    'Basketball Champions League',
    'EuroCup',
    'FIBA Europe Cup',
    'NBL Australia',
    'CBA China',
  ],
  Tennis: [
    'Wimbledon',
    'US Open',
    'Australian Open',
    'Roland Garros',
    'ATP Finals',
    'Miami Open',
    'Indian Wells',
    'Madrid Open',
    'Rome Masters',
    'Canadian Open',
    'Cincinnati Masters',
    'Shanghai Masters',
    'Paris Masters',
    'Monte Carlo Masters',
    'Hamburg Open',
    "Queen's Club Championship",
    'Halle Open',
    'ATP Cup',
    'Laver Cup',
    'Davis Cup',
  ],
  Cricket: [
    'Indian Premier League',
    'The Ashes',
    'ICC World Cup',
    'Big Bash League',
    'T20 World Cup',
    'Champions Trophy',
    'Pakistan Super League',
    'Caribbean Premier League',
    'County Championship',
    'Ranji Trophy',
    'Sheffield Shield',
    'The Hundred',
    'Vitality Blast',
    'Royal London One-Day Cup',
    'Duleep Trophy',
    'Irani Cup',
    'Border-Gavaskar Trophy',
    "Women's Cricket World Cup",
    'U19 Cricket World Cup',
    'Test Championship',
  ],
  MMA: [
    'UFC',
    'Bellator',
    'ONE Championship',
    'PFL',
    'Rizin Fighting Federation',
    'KSW',
    'Cage Warriors',
    'LFA',
    'Invicta FC',
    'BKFC',
    'Eagle FC',
    'Road FC',
    'DEEP',
    'Pancrase',
    'King of the Cage',
    'Strikeforce Legacy',
    'WEC Classic',
    'PRIDE Legends',
    'M-1 Global',
    'Fight Nights Global',
  ],
  FIFA: [
    'FIFA eWorld Cup',
    'FIFAe Nations Cup',
    'EA Sports FIFA Global Series',
    'FIFA eClub World Cup',
    'Ultimate Team Championship',
    'FIFA Pro League',
    'eChampions League',
    'ePremier League',
    'eLa Liga',
    'eSerie A',
    'eBundesliga',
    'eLigue 1',
    'MLS eMLS Cup',
    'FIFA Mobile World Cup',
    'Continental Cup',
    'Regional Championships',
    'National eFootball League',
    'FIFA Online Tournament',
    'Virtual Copa America',
    'Digital Euro Championship',
  ],
  'Table Tennis': [
    'ITTF World Tour',
    'WTT Champions',
    'Asian Championships',
    'European Championships',
    'World Table Tennis Championships',
    'Olympics Table Tennis',
    'Commonwealth Games TT',
    'WTT Cup Finals',
    'WTT Contender Series',
    'WTT Star Contender',
    'ITTF World Youth Championships',
    'European Table Tennis Union Cup',
    'Asian Table Tennis Championships',
    'Pan American Championships',
    'African Championships',
    'ITTF Challenge Series',
    'World Junior Championships',
    'World Cadet Championships',
    'Veterans World Championships',
    'University World Championships',
  ],
};

const ACTIONS = ['deposit', 'withdrawal'];

// Utility functions
function getRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function generateRandomBet(): number {
  // 80% chance for integer values, 20% chance for decimal values
  const isInteger = Math.random() < 0.8;

  if (isInteger) {
    // Generate integer values between 10 and 1000
    return Math.floor(Math.random() * 990 + 10);
  } else {
    // Generate decimal values between 10 and 500
    return parseFloat((Math.random() * 490 + 10).toFixed(2));
  }
}

function generateRandomMultiplier(): string {
  return (Math.random() * 4 + 1).toFixed(2);
}

function generateRandomOdds(): string {
  return (Math.random() * 2.5 + 1.1).toFixed(2);
}

// ID counter for unique row generation
let idCounter = 10;

function generateCasinoBetRow() {
  const id = ++idCounter;
  const user = getRandom(PLAYERS);
  const game = getRandom(CASINO_GAMES);
  const bet = formatAmount(generateRandomBet(), 'USD', true);
  const multiplier = generateRandomMultiplier();
  const payout = formatAmount(generateRandomBet() * parseFloat(multiplier), 'USD', true, true);

  return {
    id,
    user,
    game,
    betAmount: bet.toString(),
    multiplier: `${multiplier}x`,
    payout,
    action: getRandom(ACTIONS),
    gameType: 'casino' as const,
  };
}

function generateSportsBetRow() {
  const id = ++idCounter;
  const user = getRandom(PLAYERS);
  const sport = getRandom(Object.keys(SPORT_EVENT_MAP));
  const event = getRandom(SPORT_EVENT_MAP[sport]);
  const bet = formatAmount(generateRandomBet(), 'USD', true);
  const odds = generateRandomOdds();
  const payout = formatAmount(generateRandomBet() * parseFloat(odds), 'USD', true, true);

  return {
    id,
    user,
    sport,
    event,
    betAmount: bet.toString(),
    payout,
  };
}

function generateFakeRow(selectedTab: tabKeyType) {
  switch (selectedTab) {
    case TAB_KEYS.CASINO_BETS:
      return generateCasinoBetRow();
    case TAB_KEYS.SPORTS_BETS:
      return generateSportsBetRow();
    default:
      throw new Error(`Unknown tab type: ${selectedTab}`);
  }
}

export const fetchNewRow = async (selectedTab: tabKeyType) => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  return generateFakeRow(selectedTab);
};
